import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Inline } from "@/lib/inline";
import {
  Page, PageHeader, SectionTitle, Card, StatusBadge, Bar, Avatar, Chip, PrivacyNote, Loading, Label11, Tag,
  usd, int, plural, pct, ago, shortDate,
} from "@/components/common";
import { Button } from "@/components/ui/button";
import { EngineWithLogo } from "@/components/ProviderLogo";
import GetStarted from "@/components/GetStarted";
import OverviewOwed from "@/components/OwedBanner";
import { ReliabilityCard, DeliveryCard } from "@/components/OverviewInsights";
import { BorderTrail, TextLoop, TextScramble } from "@/components/Motion";
import { useInvestors } from "@/components/Investors";
import { money } from "@/pages/owner/ObjectivesMoney";
import { EventDetail, TYPE_LABEL, eventCost, clock } from "@/components/ActivityEvent";
import { Server, TriangleAlert, CircleCheck, Plus } from "lucide-react";

// design_handoff_crewbench/screens/Overview.dc.html: who is working right now, and everything waiting on the founder.
// GET /overview serves it all: what waits on this founder (change reports they haven't opened, memory proposals, full
// budgets), the budgets with what they've used, the workspace server and today's sessions. Metadata only. Reliability,
// Delivery times and the month's money (design_handoff_team_roles/) come from GET /overview/insights, less often.
const RUNNER_KIND = { standard_vm: "Standard cloud server", cloud_vm: "Cloud server", dedicated: "Dedicated server",
                      local_demo: "Local demo server" };
const LEVEL = { company: "Company", project: "Project", objective: "Objective", coder_daily: "Daily", coder_monthly: "Monthly" };
const STATUS_ORDER = { active: 0, locked: 1, ended: 2 };

const now = () => new Date();
const month = (d = now()) => d.toLocaleDateString("en-GB", { month: "long" });
const daysLeft = (d = now()) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate() - d.getDate();

function useClock() {
  const [t, setT] = useState(now());
  useEffect(() => { const i = setInterval(() => setT(now()), 30000); return () => clearInterval(i); }, []);
  return t;
}

// "2 sessions running, 1 locked." from the live counts
function runningLine(active, locked) {
  if (!active && !locked) return "No sessions running.";
  const run = active ? `${plural(active, "session")} running` : "No sessions running";
  return locked ? `${run}, ${int(locked)} locked.` : `${run}.`;
}

function waitingLine(n) {
  if (!n) return "Nothing is waiting on you.";
  return n === 1 ? "1 thing is waiting on you." : `${int(n)} things are waiting on you.`;
}

// a full budget, as the founder reads it on the Overview
function budgetCard(b) {
  const spent = `${usd(b.spent_usd)} of ${usd(b.amount_usd)}`;
  switch (b.level) {
    case "coder_daily": return [`${b.name} reached their daily budget`, `${spent} today. Their sessions can't start new turns until tomorrow.`];
    case "coder_monthly": return [`${b.name} reached their monthly budget`, `${spent} in ${month()}. Their sessions can't start new turns until next month.`];
    case "company": return ["The company reached its monthly budget", `${spent} in ${month()}. No session can start a new turn until you raise it.`];
    case "project": return [`${b.name} reached its monthly budget`, `${spent} in ${month()}. Its sessions can't start new turns until you raise it.`];
    default: return [`${b.name} reached its budget`, `${spent}. Its sessions can't start new turns until you raise it.`];
  }
}

// a proposal's first sentence is its title, the rest its body
function splitEntry(text) {
  const t = (text || "").trim();
  const m = t.match(/^(.+?[.!?])\s+([\s\S]+)$/);
  return m ? [m[1].replace(/[.!]$/, ""), m[2]] : [t, ""];
}

function WaitingRow({ eyebrow, title, children, actions, testid }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-5 py-[18px] border-t first:border-t-0" data-testid={testid}>
      <div className="flex flex-col gap-1.5 min-w-0 flex-1 basis-80">
        <Label11>{eyebrow}</Label11>
        <span className="font-display text-[15px] leading-[1.35] tracking-[0.02em] font-semibold">{title}</span>
        {children}
      </div>
      <div className="flex gap-2 shrink-0">{actions}</div>
    </div>
  );
}

function Waiting({ data, names }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const decide = useMutation({
    mutationFn: async ({ id, action }) => api.post(`/memory/proposals/${id}/${action}`),
    onSuccess: (_, { action, project }) => {
      ["overview", "overview-counts", "proposals", "memory"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(action === "approve" ? `Approved. New sessions on ${project} start with it.` : "Rejected. It won't be added.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const { reports, proposals, budgets, terms, messages } = data.waiting;
  if (!reports.length && !proposals.length && !budgets.length && !terms.length && !messages.length) return (
    <Card className="px-6 py-8 flex flex-col gap-2" data-testid="nothing-waiting">
      <div className="flex items-center gap-2 text-gr-success font-semibold"><CircleCheck className="w-4 h-4" />Nothing to decide right now</div>
      <p className="m-0 max-w-[480px] text-gr-ink-secondary [text-wrap:pretty]">
        Change reports, memory proposals, budget limits, hiring terms and new messages show up here as soon as they need you. Every budget has room.
      </p>
      <Link to="/founder/reports" className="mt-2 self-start text-sm underline underline-offset-2 hover:opacity-50">See the latest change reports</Link>
    </Card>
  );
  return (
    <div className="rounded-[10px] bg-card overflow-hidden [&_code]:bg-transparent [&_code]:px-0 [&_code]:text-[12px]" data-testid="waiting-list">
      {reports.map((r, i) => (
        <WaitingRow key={r.id} eyebrow="Change report" title={names.objectives[r.objective_id] || "No objective"} testid="waiting-report"
          actions={<Button variant={i === 0 ? "default" : "outline"} onClick={() => navigate(`/founder/reports?report=${r.id}`)}>Review changes</Button>}>
          {r.summary_line && <span className="text-gr-ink-secondary [text-wrap:pretty]">{r.summary_line}</span>}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground tabular-nums">
            <span>{names.people[r.coder_id] || "Someone"}, {ago(r.created_at)}</span><span>·</span>
            <span className="font-mono text-gr-ink-secondary">{r.branch}</span><span>·</span>
            <span>{plural(r.totals.files, "file")} <span className="text-gr-success">+{int(r.totals.added_lines)}</span> <span className="text-gr-danger">−{int(r.totals.removed_lines)}</span></span>
          </div>
          {r.flags.length ? (
            <div className="flex items-center gap-1.5 text-sm text-gr-warning">
              <TriangleAlert className="w-3.5 h-3.5 shrink-0" /><span>Check before merging: {r.flags.join(", ")}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-sm text-gr-success"><CircleCheck className="w-3.5 h-3.5 shrink-0" /><span>No flagged paths</span></div>
          )}
        </WaitingRow>
      ))}
      {proposals.map((p) => {
        const project = names.projects[p.project_id] || "this project";
        const [title, body] = splitEntry(p.entry);
        return (
          <WaitingRow key={p.id} eyebrow={`Shared memory · ${project}`} title={<Inline text={title} />} testid="waiting-proposal"
            actions={<>
              <Button variant="ghost" className="text-gr-ink-secondary" disabled={decide.isPending}
                onClick={() => decide.mutate({ id: p.id, action: "reject", project })}>Reject</Button>
              <Button variant="outline" disabled={decide.isPending}
                onClick={() => decide.mutate({ id: p.id, action: "approve", project })}>Approve</Button>
            </>}>
            {body && <span className="text-gr-ink-secondary [text-wrap:pretty]"><Inline text={body} /></span>}
            <span className="text-sm text-muted-foreground">
              Proposed by {names.people[p.coder_id] || "someone"}, {ago(p.created_at)}. Once approved, every new session on {project} starts with it.
            </span>
          </WaitingRow>
        );
      })}
      {budgets.map((b) => {
        const [title, body] = budgetCard(b);
        return (
          <WaitingRow key={`${b.level}:${b.ref_id}`} eyebrow="Budget" title={title} testid="waiting-budget"
            actions={<Button variant="outline" onClick={() => navigate("/founder/budgets")}>Raise limit</Button>}>
            <span className="text-gr-ink-secondary [text-wrap:pretty] tabular-nums">{body}</span>
            {(b.session || b.since) && (
              <span className="text-sm text-muted-foreground">
                {[b.session && (names.objectives[b.session.objective_id] || "No objective"), b.session && `session ${b.session.short_id}`,
                  b.since && ago(b.since)].filter(Boolean).join(" · ")}
              </span>
            )}
          </WaitingRow>
        );
      })}
      {messages.map((m) => (
        <WaitingRow key={m.thread_id} testid="waiting-message"
          eyebrow="Message" title={<span className="inline-flex flex-wrap items-center gap-2">{names.people[m.sender_id] || "Someone"} wrote to you
            {names.roles[m.sender_id] === "viewer" && <Tag tone="info">Investor</Tag>}</span>}
          actions={<Button variant="outline" onClick={() => navigate(`/founder/messages/${m.thread_id}`)}>Open message</Button>}>
          <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{m.subject} · {new Date(m.at).toDateString() === new Date().toDateString() ? clock(m.at) : shortDate(m.at)}{m.unread > 1 ? ` · ${m.unread} new` : ""}</span>
        </WaitingRow>
      ))}
      {terms.map((t) => (
        <WaitingRow key={t.id} eyebrow="Hiring terms" title={`${names.people[t.person_id] || "Someone"} hasn't agreed yet`} testid="waiting-terms"
          actions={<Button variant="outline" onClick={() => navigate("/founder/team")}>Open Team</Button>}>
          <span className="text-sm text-muted-foreground tabular-nums">
            {[names.objectives[t.objective_id] || "An objective", t.kind === "new" ? `${money(t.fee_usd)} fixed fee` : `fee change to ${money(t.fee_usd)}`,
              `sent ${shortDate(t.at)}`].join(" · ")}
          </span>
        </WaitingRow>
      ))}
    </div>
  );
}

function BudgetsPanel({ budgets, spend }) {
  // as the handoff shows them: the company's month, the fullest project and objective, then each person's budgets
  // (their day, and their month where one is set)
  const named = (b) => (b.level === "company" ? month() : b.name);
  const fullest = (level) => budgets.filter((b) => b.level === level && !(level === "objective" && b.status === "done"))
    .sort((a, b) => b.spent_usd / (b.amount_usd || 1) - a.spent_usd / (a.amount_usd || 1))[0];
  const people = ["coder_daily", "coder_monthly"].flatMap((level) => budgets.filter((b) => b.level === level));
  const rows = [fullest("company"), fullest("project"), fullest("objective"), ...people].filter(Boolean);
  return (
    <Card className="flex flex-col gap-4 tabular-nums" data-testid="overview-budgets">
      <div className="flex justify-between items-baseline gap-2">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Budgets</span>
        <span className="text-sm text-muted-foreground">{plural(daysLeft(), "day")} left in {month()}</span>
      </div>
      {rows.length === 0 && (
        <span className="text-sm text-muted-foreground">No budgets yet. <Link to="/founder/budgets" className="underline underline-offset-2 hover:opacity-50">Set one</Link></span>
      )}
      {rows.map((b) => {
        const p = pct(b.spent_usd, b.amount_usd);
        const full = b.amount_usd > 0 && b.spent_usd >= b.amount_usd;
        return (
          <div key={`${b.level}:${b.ref_id}`} className="flex flex-col gap-2">
            <div className="flex flex-col"><Label11>{LEVEL[b.level]}</Label11><span className="text-sm font-semibold truncate">{named(b)}</span></div>
            <Bar pct={p} full={full} />
            {full ? (
              <div className="flex justify-between gap-2 text-sm text-gr-danger"><span>Budget reached</span><span>{usd(b.spent_usd)} · {p}%</span></div>
            ) : (
              <div className="flex justify-between gap-2 text-sm"><span className="text-muted-foreground">{usd(b.spent_usd)} of {usd(b.amount_usd)}</span><span>{p}%</span></div>
            )}
          </div>
        );
      })}
      {spend?.total_usd > 0 && (
        <p className="m-0 text-sm text-muted-foreground tabular-nums">
          Agreed fees this month: {money(spend.fees_usd)}. Total cost with AI: {usd(spend.total_usd)}. <Link to="/founder/objectives?view=money"
            className="underline underline-offset-2 hover:opacity-50">Money per objective</Link>
        </p>
      )}
    </Card>
  );
}

function ServerPanel({ data }) {
  const r = data.runner;
  const running = r ? (r.max_active_sessions ? `${int(data.active_sessions)} of ${plural(r.max_active_sessions, "session")}` : plural(data.active_sessions, "session"))
    : null;
  return (
    <Card className="relative flex flex-col gap-3" data-testid="overview-runner-card">
      {r?.status === "online" && <BorderTrail />}
      <div className="flex justify-between items-center gap-2">
        <span className="flex items-center gap-2 font-display text-[15px] tracking-[0.02em] font-semibold"><Server className="w-4 h-4" />Workspace server</span>
        {r && <StatusBadge status={r.status} />}
      </div>
      {r ? (
        <div className="flex flex-col gap-2 text-sm tabular-nums">
          <div className="flex justify-between gap-2"><span className="text-muted-foreground">Size</span><span>{r.size_label || RUNNER_KIND[r.kind] || r.kind}</span></div>
          <div className="flex justify-between gap-2"><span className="text-muted-foreground">Running</span><span>{running}</span></div>
          <div className="flex justify-between gap-2"><span className="text-muted-foreground">Last heartbeat</span><span>{ago(r.last_heartbeat_at)}</span></div>
        </div>
      ) : <span className="text-sm text-muted-foreground">No workspace server yet.</span>}
      {(data.plan_ends_on || data.server_deletes_on) && (  // docs/ACCOUNT_LIFECYCLE_PLAN.md L1
        <div className="pt-3 border-t text-sm text-gr-warning flex gap-2" data-testid="overview-closing">
          <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="flex flex-col gap-0.5">
            {data.plan_ends_on && <span>Your plan ends on {new Date(data.plan_ends_on).toLocaleDateString()}.</span>}
            {data.server_deletes_on && <span>This server is deleted on {new Date(data.server_deletes_on).toLocaleDateString()}.</span>}
            <Link to="/founder/store" className="underline underline-offset-2 hover:opacity-50">Keep it in Store</Link>
          </div>
        </div>
      )}
    </Card>
  );
}

const SESSION_COLS = "grid grid-cols-[minmax(240px,1.5fr)_132px_184px_96px_128px_88px] gap-4 min-w-[980px]";

function SessionsToday({ data, names }) {
  const sessions = [...data.sessions].sort((a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3)
    || new Date(b.last_activity_at) - new Date(a.last_activity_at));
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle note={`${int(data.sessions_this_month)} this month`}>Sessions today</SectionTitle>
      <div className="rounded-[10px] bg-card overflow-x-auto tabular-nums" data-testid="overview-sessions">
        <div className={`${SESSION_COLS} px-4 py-2.5 text-xs uppercase tracking-[0.09em] text-muted-foreground`}>
          <span>Person</span><span>Session</span><span>Engine</span><span>Status</span><span>Spend of cap</span><span className="text-right">Last active</span>
        </div>
        {sessions.length === 0 && <div className="px-4 py-3 border-t text-sm text-muted-foreground min-w-[980px]">No sessions today.</div>}
        {sessions.map((s) => (
          <div key={s.id} className={`${SESSION_COLS} items-center px-4 py-3 border-t`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar name={names.people[s.coder_id]} />
              <div className="flex flex-col min-w-0">
                <span className="font-semibold truncate">{names.people[s.coder_id] || "—"}</span>
                <span className="text-sm text-muted-foreground truncate">{names.objectives[s.objective_id] || "No objective"}</span>
              </div>
            </div>
            <div className="flex flex-col min-w-0"><TextScramble>{s.short_id}</TextScramble><span className="font-mono text-xs text-muted-foreground truncate">{s.branch}</span></div>
            <EngineWithLogo engine={s.engine} className="text-sm [&_svg]:w-3.5 [&_svg]:h-3.5" />
            <span><StatusBadge status={s.status} live /></span>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm">{usd(s.spent_usd)} <span className="text-muted-foreground">of {usd(s.budget_cap_usd)}</span></span>
              <Bar pct={pct(s.spent_usd, s.budget_cap_usd)} full={s.budget_cap_usd > 0 && s.spent_usd >= s.budget_cap_usd} />
            </div>
            <span className="text-sm text-muted-foreground text-right">{ago(s.last_activity_at)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ActivityFeed({ names }) {
  const { data } = useQuery({ queryKey: ["activity", "overview"], refetchInterval: 10000,
                              queryFn: async () => (await api.get("/activity", { params: { limit: 6 } })).data });
  const rows = data?.timeline || [];
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle note="Metadata only, newest first">Activity</SectionTitle>
      <div className="rounded-[10px] bg-card overflow-hidden text-sm tabular-nums" data-testid="overview-activity">
        {rows.length === 0 && <div className="px-4 py-2.5 text-muted-foreground">Nothing yet.</div>}
        {rows.map((e, i) => (
          <div key={i} className="grid grid-cols-[44px_76px_minmax(0,1fr)_64px] gap-3 items-center px-4 py-2.5 border-t first:border-t-0">
            <span className="text-muted-foreground">{clock(e.ts)}</span>
            <span><Chip>{TYPE_LABEL[e.type]}</Chip></span>
            <span className="min-w-0 truncate"><EventDetail e={e} names={names} /></span>
            <span className={e.type === "usage" ? "text-right" : "text-right text-muted-foreground"}>{eventCost(e)}</span>
          </div>
        ))}
      </div>
      <PrivacyNote>You see files, lines, commits and spend. Prompts and chat stay with the person who wrote them.</PrivacyNote>
    </section>
  );
}

export default function Overview() {
  const t = useClock();
  const { data, isLoading } = useQuery({
    queryKey: ["overview"],
    queryFn: async () => (await api.get("/overview")).data,
    refetchInterval: 10000,
  });
  // under "overview", so whatever refreshes the Overview refreshes these too
  const inv = useInvestors();  // FEATURE_INVESTORS: what the cards say about sharing
  const { data: more } = useQuery({ queryKey: ["overview", "insights"], refetchInterval: 60000,
                                    queryFn: async () => (await api.get("/overview/insights")).data });
  // the live line takes turns with how fresh the page is and what it shows; screen readers get the date and time
  const eyebrow = (
    <TextLoop>
      <span>Live · {t.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}, {clock(t)}</span>
      <span>Refreshes every 10 seconds</span>
      <span>Metadata only. Prompts and chat stay private.</span>
    </TextLoop>
  );
  const action = <Button asChild variant="outline"><Link to="/founder/objectives?new=1"><Plus />New objective</Link></Button>;
  if (isLoading || !data) return (
    <Page><PageHeader eyebrow={eyebrow} live title="Overview" actions={action} /><Loading /></Page>
  );

  const names = {
    people: Object.fromEntries(data.people.map((p) => [p.id, p.name])),
    roles: Object.fromEntries(data.people.map((p) => [p.id, p.role])),
    objectives: Object.fromEntries(data.objectives.map((o) => [o.id, o.title])),
    projects: Object.fromEntries(data.projects.map((p) => [p.id, p.name])),
  };
  const w = data.waiting;
  const waiting = w.reports.length + w.proposals.length + w.budgets.length + w.terms.length + w.messages.length;

  return (
    <Page data-testid="owner-overview">
      <PageHeader eyebrow={eyebrow} live title={runningLine(data.active_sessions, data.locked_sessions)}
        status={waitingLine(waiting)} actions={action} />
      <OverviewOwed />
      <GetStarted />
      <div className="flex flex-wrap gap-5 items-start">
        <div className="flex-[100_1_480px] min-w-0"><Waiting data={data} names={names} /></div>
        <div className="flex-[1_0_300px] max-w-full flex flex-col gap-4"><ServerPanel data={data} /></div>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(440px,100%),1fr))] gap-4 items-start">
        {more && <ReliabilityCard r={more.reliability} inv={inv} />}
        <BudgetsPanel budgets={data.budgets} spend={more?.money} />
        {more && <DeliveryCard d={more.delivery} inv={inv} />}
      </div>
      <SessionsToday data={data} names={names} />
      <ActivityFeed names={names} />
    </Page>
  );
}
